import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { sanitizeDates } from './lib/date-range';
import Label from './Label';
import DateInput from './DateInput';

const Wrapper = styled.div`
  align-items: center;
  display: flex;
`;

/**
 * NOTE: These custom label styles parallel the label styles in the current
 *       date range picker. Not sure if we want to keep these custom styles
 *       instead of the default Label styles, but included them for now for
 *       consistency.
 */
const DateLabel = styled(Label)`
  color: var(--datepicker-label);
  font-size: var(--x-small);
  font-weight: var(--normal);
  width: 100%;

  > :first-child {
    margin-top: 4px;
  }
`;

const Separator = styled.span`
  color: var(--datepicker-separator);
  padding-top: 16px;

  ::before {
    content: '-';
    margin: 0 8px;
  }
`;

function DateRangeInputs({
  className,
  disabled = false,
  endProps = {},
  onChange,
  onDayClick,
  portal = false,
  startProps = {},
  value: { start, end } = {},
}) {
  // Consolidated change handler
  const handleChangeFor = changed => value => {
    const next = { start, end, [changed]: value };
    const sanitized = sanitizeDates(next, changed);
    onChange?.(sanitized, changed);
  };

  // Consolidated day click handler
  const handleDayClickFor =
    changed =>
    (value, ...rest) => {
      const next = { start, end, [changed]: value };
      const sanitized = sanitizeDates(next, changed);
      onDayClick?.(sanitized, changed, ...rest);
    };

  return (
    <Wrapper className={className}>
      <DateLabel htmlFor="start">
        Start
        <DateInput
          {...startProps}
          inputProps={{
            ...startProps.inputProps,
            'aria-label': 'Start date',
            name: 'start',
            disabled,
          }}
          onChange={handleChangeFor('start')}
          onDayClick={handleDayClickFor('start')}
          portal={portal}
          value={start}
        />
      </DateLabel>

      <Separator />

      <DateLabel htmlFor="end">
        End
        <DateInput
          {...endProps}
          inputProps={{
            ...endProps.inputProps,
            'aria-label': 'End date',
            name: 'end',
            disabled,
          }}
          onChange={handleChangeFor('end')}
          onDayClick={handleDayClickFor('end')}
          portal={portal}
          value={end}
        />
      </DateLabel>
    </Wrapper>
  );
}

DateRangeInputs.propTypes = {
  // Passthrough props for start/end date inputs
  /* eslint-disable react/forbid-prop-types */
  startProps: PropTypes.object,
  endProps: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */

  // Consolidated handlers for start/end date inputs
  onChange: PropTypes.func,
  onDayClick: PropTypes.func,

  className: PropTypes.string,
  disabled: PropTypes.bool,
  portal: PropTypes.bool,
  value: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
  }),
};

export default styled(DateRangeInputs)``;
